<template>
  <div class="selected-email-assignment-container" v-if="emailData.subject || assignment && Object.keys(assignment).length > 0">
    <div class="card-header" @click="toggleCollapse">
      <div class="header-content">
        <IconProvider v-if="showCollapseIcon !== false" :name="isCollapsed ? 'ChevronDown' : 'ChevronUp'" :size="16" class="chevron-icon" />
        <span class="title">{{ getTitle }}</span>
      </div>
    </div>
    
    <div class="card-content" v-show="!isCollapsed">
      <!-- Email Section -->
      <div class="email-section" v-if="emailData.subject">
        <div class="email-subject">{{ emailData.subject || 'No Subject' }}</div>
        <div class="email-details">
          <div class="email-received">Received: {{ formatDate(emailData.received) }}</div>
          <div class="email-from">{{ emailData.fromName || 'No Sender' }} &lt;{{ emailData.fromEmail || 'No Email' }}&gt;</div>
        </div>
      </div>
      <AssignmentCard
        v-if="assignment && Object.keys(assignment).length > 0"
        :assignment="assignment"
        :show-link="true"
        :enableDialogTrigger="true"
        :showActiveLabel="true"
        @open-dialog="openAssignmentDialog"
        @select="handleAssignmentPreselect"
        @assignmentChanged="$emit('assignmentChanged', $event)"
      />
      <AssignmentDialog
        v-if="showDialog"
        :visible="showDialog"
        :loadAssignments="true"
        @close="showDialog = false"
        @selectAssignment="handleAssignmentSelection"
      />
    </div>
  </div>
</template>

<script>
import AssignmentCard from '../actions/assignment/AssignmentCard.vue';
import AssignmentDialog from '../actions/assignment/AssignmentDialog.vue';
import IconProvider from './IconProvider.vue';
import { getPropertyAddressOrName, formatDate as formatDateString } from '@/utils/globalUtil.js';
import { mapActions } from 'vuex';

export default {
  name: 'SelectedEmailAndAssignment',
  components: {
    IconProvider,
    AssignmentCard,
    AssignmentDialog
  },
  props: {
    emailData: {
      type: Object,
      default: () => ({})
    },
    assignment: {
      type: Object,
      default: () => ({})
    },
    initiallyCollapsed: {
      type: Boolean,
      default: false
    },showCollapseIcon: {
      type: Boolean, 
      default: true
    },
    disableAssignmentClick: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isCollapsed: this.initiallyCollapsed,
      showDialog: false,
      selectedAssignment: null
    };
  },
  computed: {
    getTitle() {
      const hasEmail = this.emailData && this.emailData.subject;
      const hasAssignment = this.assignment && Object.keys(this.assignment).length > 0;
      
      if (hasEmail && hasAssignment) {
        return 'Selected Email and Assignment';
      } else if (hasAssignment) {
        return 'Selected Assignment';
      } else if (hasEmail) {
        return 'Selected Email';
      }
      return '';
    }
  },
  methods: {
    ...mapActions({
      setSelectedAssignment: 'assignments/selectAssignment'
    }),
    toggleCollapse() { 
      if(this.showCollapseIcon!==false) {
        this.isCollapsed = !this.isCollapsed;
        this.$emit('collapseToggled', this.isCollapsed);
      }
    },
    formatDate(dateString) {
      return formatDateString(dateString);
    },
    getFormattedPropertyAddressOrName(assignment, needPropertyName) {
      return getPropertyAddressOrName(assignment, needPropertyName);
    },
    openAssignmentDialog() {
      if (this.disableAssignmentClick) {
        return; // Do nothing if clicks are disabled
      }
      // show dialog to select assignment
      this.showDialog = true;
    },
    handleAssignmentPreselect(assignment) {
      this.selectedAssignment = assignment;
    },
    handleAssignmentSelection(assignment) {
      this.selectedAssignment = assignment;
      this.setSelectedAssignment(assignment);
      this.showDialog = false;
      this.$emit('assignmentChanged', assignment);
    }
  },
  emits: ['assignmentChanged', 'collapseToggled']
};
</script>

<style scoped>
.selected-email-assignment-container {
  border-radius: 4px;
  margin-bottom: 9px;
  overflow: hidden;
  background: #003F2D1A;
}

.card-header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.chevron-icon {
  color: #666666;
  transition: transform 0.3s ease;
}

.title {
  font-family: Calibre-R;
  font-weight: 400;
  font-size: 16px;
  letter-spacing: 0.5%;
  color: #000000;
}

.card-content {
  padding: 0px 10px 0px 10px;
}

.email-section {
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 16px;
}

.email-subject {
  font-family: Calibre-R-Medium;
  font-weight: 500;
  font-size: 14px;
  line-height: 18px;
  color: #000000;
  margin-bottom: 8px;
}

.email-details {
  font-size: 12px;
  color: #666666;
}

.email-received {
  margin-bottom: 4px;
  color: #1A1A1A;
  opacity: 0.65;
}

.email-from {
  color: #000000;
  font-weight: 400;
}

.assignment-section {
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 16px;
}

.property-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.property-title {
  font-weight: 600;
  font-size: 18px;
  line-height: 22px;
  color: #000000;
}

.status-badge {
  background: #e8f5e8;
  color: #2e7d32;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.client-address {
  font-size: 16px;
  line-height: 20px;
  color: #666666;
  margin-bottom: 20px;
}

.assignment-details {
  display: flex;
  gap: 32px;
}

.due-date-row {
  padding-top: 12px;
}

.details-left {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.details-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-item {
  display: flex;
  flex-direction: row;
  align-items: baseline;
  gap: 8px;
  min-height: 20px;
}

.label {
  font-size: 14px;
  line-height: 18px;
  color: #666666;
  font-weight: 400;
  white-space: nowrap;
  flex-shrink: 0;
}

.value {
  font-size: 16px;
  line-height: 20px;
  color: #000000;
  font-weight: 400;
  flex: 1;
}

.underlined {
  text-decoration: underline;
}

.mt-2 {
  margin-top: 8px;
}

/* Media query for containers narrower than 600px - switch to vertical layout */
@media (max-width: 600px) {
  .detail-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }
  
  .label {
    white-space: normal;
  }
}

/* When assignment details stack but container is still wide enough for horizontal labels */
@media (max-width: 480px) and (min-width: 400px) {
  .assignment-details {
    flex-direction: column;
    gap: 16px;
  }
  
  .detail-item {
    flex-direction: row;
    align-items: baseline;
    gap: 8px;
  }
}

/* Responsive design */
@media (max-width: 480px) {
  .card-header {
    padding: 12px 8px;
  }
  
  .title {
    font-size: 13px;
  }
  
  .email-section,
  .assignment-section {
    padding: 10px;
  }
  
  .assignment-details {
    flex-direction: column;
    gap: 16px;
  }
  
  .property-title {
    font-size: 16px;
  }
  
  .client-address {
    font-size: 14px;
  }
  
  .detail-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }
}

@media (max-width: 320px) {
  .card-header {
    padding: 8px 10px;
  }
  
  .card-content {
    padding: 10px;
  }
  
  .email-section,
  .assignment-section {
    padding: 8px;
    margin-bottom: 12px;
  }
  
  .assignment-details {
    flex-direction: column;
    gap: 12px;
  }
  
  .property-title {
    font-size: 14px;
  }
  
  .client-address {
    font-size: 13px;
  }
}
</style>